import {
  CODING_AGENT_LABELS,
  canOpenCodingAgent,
  cardDismissalLabel,
  canStartCodingAgent,
  codingAgentAccessChoices,
  codingAgentAccessLabel,
  codingAgentActionCardKind,
  codingAgentSessionToContinue,
  isCodingAgentRunning,
  type CodingAgentContent,
  type CodingAgentRun,
} from '@littlebird/action-cards';
import { type ActionCard, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { type ReactNode } from 'react';

import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { AgentLogo } from '@/features/coding-agents/agent-logos';

import {
  codingAgentContentOf,
  openCodingAgent,
  runsElsewhere,
  saveCodingAgentEdit,
  startCodingAgent,
  startingCards,
  startQuestions,
  stopCodingAgent,
  type StartChoices,
} from '../coding-agent-runs';

// What the Start button says while a start waits on the user
const CHOICE_LABELS: Record<keyof StartChoices, string> = {
  startAnyway: 'Start anyway',
  trustFolder: 'Trust folder and start',
};

const RUN_STATUS: Record<CodingAgentRun['state'], string> = {
  starting: 'Starting…',
  working: 'Working…',
  finished: 'Finished',
  failed: 'Failed',
  stopped: 'Stopped',
};

// Start, stop and open happen on this device, so this card is drawn here and not by the server
export const CodingAgentCard = observer(
  ({
    card,
    sourceChatTag,
  }: {
    card: ActionCard;
    sourceChatTag?: ReactNode;
  }) => {
    // The run arrives by sync on the same MobX card, so React Compiler must not cache this
    'use no memo';
    const { actionCards } = useStores();
    const content = codingAgentContentOf(card);
    if (!content) return null;

    const { run } = content;
    const agent = CODING_AGENT_LABELS[content.agent];
    const pending = card.status === 'draft';
    const starting = startingCards.has(card.id);
    const running = isCodingAgentRunning(content);
    const elsewhere = running && run ? runsElsewhere(run) : false;
    const startable = pending && !starting && canStartCodingAgent(content);
    const question = startable ? startQuestions.get(card.id) : undefined;
    const error = startable && run?.state === 'failed' ? run.error : undefined;
    const title = codingAgentActionCardKind.getSummary(content);
    const access = codingAgentAccessLabel(content.agent, content.access);
    const choices = codingAgentAccessChoices(content.agent);
    const status =
      (card.status === 'dismissed' && cardDismissalLabel(card)) ||
      (card.status === 'revoked' && 'Revoked') ||
      (starting && RUN_STATUS.starting) ||
      (elsewhere && 'Working on another computer') ||
      (run && RUN_STATUS[run.state]);

    return (
      <YStack
        className="w-full gap-3 overflow-hidden rounded-2xl border border-border-strong bg-surface p-4"
        aria-label={title}
      >
        <XStack className="items-center gap-2">
          <AgentLogo id={content.agent} className="size-5 shrink-0" />
          <span className="min-w-0 flex-1 truncate font-medium text-foreground">
            {title}
          </span>
          {status ? (
            <span role="status" className="text-xs text-foreground-muted">
              {status}
            </span>
          ) : null}
          {sourceChatTag}
        </XStack>
        <p className="line-clamp-6 whitespace-pre-wrap text-sm text-foreground">
          {content.prompt}
        </p>
        {startable ? (
          <XStack className="items-center gap-2">
            <Input
              key={content.cwd}
              aria-label="Folder"
              variant="ghost"
              inputSize="sm"
              className="min-w-0 flex-1"
              defaultValue={content.cwd}
              onBlur={(event) => {
                const cwd = event.target.value.trim();
                // A folder the backend would refuse is put back, so the card stays as it was
                if (
                  /^(~\/|\/)[^/]/.test(cwd) &&
                  !cwd.split('/').includes('..')
                ) {
                  void saveCodingAgentEdit(card.id, { cwd });
                } else event.target.value = content.cwd;
              }}
            />
            {choices.length > 1 ? (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button variant="outline" size="sm" aria-label="Access">
                    {access}
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">
                  <DropdownMenu.RadioGroup
                    value={content.access}
                    onValueChange={(picked) =>
                      void saveCodingAgentEdit(card.id, {
                        access: picked as CodingAgentContent['access'],
                      })
                    }
                  >
                    {choices.map((choice) => (
                      <DropdownMenu.RadioItem key={choice} value={choice}>
                        {codingAgentAccessLabel(content.agent, choice)}
                      </DropdownMenu.RadioItem>
                    ))}
                  </DropdownMenu.RadioGroup>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            ) : (
              <span className="text-xs text-foreground-muted">{access}</span>
            )}
          </XStack>
        ) : (
          <span className="truncate text-xs text-foreground-muted">
            {content.cwd} · {access}
          </span>
        )}
        {(question?.error ?? error) ? (
          <span role="alert" className="text-sm text-destructive">
            {question?.error ?? error}
          </span>
        ) : null}
        <XStack className="flex-wrap items-center justify-end gap-2 empty:hidden">
          {pending && !running && !starting ? (
            <Button
              variant="ghost"
              size="sm"
              className="mr-auto"
              onClick={() => void actionCards.dismiss(card.id)}
            >
              Dismiss
            </Button>
          ) : null}
          {canOpenCodingAgent(content) ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void openCodingAgent(card.id)}
            >
              Open in {agent}
            </Button>
          ) : null}
          {running && !elsewhere ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void stopCodingAgent(card.id)}
            >
              Stop
            </Button>
          ) : null}
          {error && codingAgentSessionToContinue(content) ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                void startCodingAgent(card.id, { newSession: true })
              }
            >
              Start a new session
            </Button>
          ) : null}
          {startable ? (
            <Button
              size="sm"
              onClick={() =>
                void startCodingAgent(
                  card.id,
                  question && { ...question.given, [question.choice]: true }
                )
              }
            >
              {question ? CHOICE_LABELS[question.choice] : 'Start'}
            </Button>
          ) : null}
        </XStack>
      </YStack>
    );
  }
);
